/**
 * Queue Metrics Hook
 *
 * Fetches and manages queue monitoring metrics for the admin dashboard.
 * Provides real-time updates via WebSocket and periodic polling.
 */

import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { apiClient } from '@/utils/apiClient';
import { useToast } from './use-toast';

export interface QueueMetrics {
  queue_depth: number;
  jobs_per_minute: number;
  average_processing_time: number;
  failure_rate: number;
  active_workers: number;
  timestamp?: string;
}

export interface PerformanceStats {
  period_hours?: number;
  data_points?: number;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  success_rate: number;
  avg_queue_depth: number;
  max_queue_depth: number;
  min_queue_depth: number;
  avg_jobs_per_minute: number;
  peak_jobs_per_minute: number;
  avg_processing_time: number;
  max_processing_time: number;
  min_processing_time: number;
  avg_failure_rate: number;
  max_failure_rate: number;
  time_series: Array<{
    timestamp: string;
    queue_depth: number;
    jobs_per_minute: number;
    processing_time: number;
    failure_rate: number;
    active_workers: number;
  }>;
  failure_trend: Array<{
    hour: string;
    avg_failure_rate: number;
    max_failure_rate: number;
  }>;
}

export interface TierMetrics {
  [tier: string]: {
    total_jobs_24h: number;
    completed_jobs: number;
    failed_jobs: number;
    queued_jobs: number;
    avg_wait_time: number;
    avg_processing_time: number;
    success_rate: number;
    user_count: number;
  };
}

export interface Alert {
  level: 'info' | 'warning' | 'critical' | 'emergency';
  type: string;
  message: string;
  value?: number;
  threshold?: number;
  created_at?: string;
}

export interface RecentIssue {
  type: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  count: number;
  timestamp: string;
}

export interface CacheMetrics {
  total_cached: number;
  active_cached: number;
  expired_cached?: number;
  cache_hits: number;
  cache_misses: number;
  hit_rate: number;
  max_cache_size?: number;
}

export interface WorkerUtilization {
  total_workers: number;
  active_tasks: number;
  max_capacity: number;
  utilization_percentage: number;
  worker_details?: {
    [worker: string]: {
      active_tasks: number;
      status: 'busy' | 'idle';
    };
  };
}

export interface ProcessingTimeDistribution {
  p50: number;
  p75: number;
  p90: number;
  p95: number;
  p99: number;
  min: number;
  max: number;
  mean: number;
  distribution: {
    [bucket: string]: number;
  };
  total_jobs: number;
}

export interface SecurityOverview {
  failed_logins_24h: number;
  failed_logins_7d: number;
  total_events_24h: number;
  events_by_type: {
    [type: string]: number;
  };
  suspicious_ips: Array<{
    ip: string;
    event_count: number;
  }>;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
}

export interface APIQuota {
  current_month: string;
  total_cost: number;
  openai_cost: number;
  openai_tokens: number;
  elevenlabs_cost: number;
  elevenlabs_characters: number;
  sendgrid_emails: number;
  daily_breakdown: Array<{
    date: string;
    total_cost: number;
    openai: number;
    elevenlabs: number;
  }>;
}

export interface ComponentHealth {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  message: string;
  workers?: string[];
}

export interface SystemHealth {
  timestamp: string;
  components: {
    database: ComponentHealth;
    redis: ComponentHealth;
    elevenlabs: ComponentHealth;
    celery: ComponentHealth;
  };
  overall: 'healthy' | 'degraded' | 'unhealthy';
}

export interface DashboardMetrics {
  timestamp: string;
  current_metrics: QueueMetrics;
  performance_history: PerformanceStats;
  alerts: Alert[];
  recent_issues: RecentIssue[];
  cache_metrics: CacheMetrics;
  worker_utilization: WorkerUtilization;
  processing_time_distribution: ProcessingTimeDistribution;
  tier_metrics?: TierMetrics;
  security_overview?: SecurityOverview;
  api_quota?: APIQuota;
}

interface UseQueueMetricsOptions {
  refreshInterval?: number | null;
  enabled?: boolean;
}

export const useQueueMetrics = (
  refreshInterval: number | null = 5000,
  options: UseQueueMetricsOptions = {}
) => {
  const { enabled = true } = options;
  const { toast } = useToast();

  const [currentMetrics, setCurrentMetrics] = useState<QueueMetrics | null>(null);
  const [performanceStats, setPerformanceStats] = useState<PerformanceStats | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [recentIssues, setRecentIssues] = useState<RecentIssue[]>([]);
  const [cacheMetrics, setCacheMetrics] = useState<CacheMetrics | null>(null);
  const [workerUtilization, setWorkerUtilization] = useState<WorkerUtilization | null>(null);
  const [processingTimeDistribution, setProcessingTimeDistribution] = useState<ProcessingTimeDistribution | null>(null);
  const [tierMetrics, setTierMetrics] = useState<TierMetrics | null>(null);
  const [securityOverview, setSecurityOverview] = useState<SecurityOverview | null>(null);
  const [apiQuota, setApiQuota] = useState<APIQuota | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  /**
   * Fetch dashboard metrics from API
   */
  const fetchMetrics = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch comprehensive dashboard metrics
      const response = await apiClient.get<DashboardMetrics>('/monitoring/metrics/dashboard');

      if (response) {
        setCurrentMetrics(response.current_metrics || null);
        setPerformanceStats(response.performance_history || null);
        setAlerts(response.alerts || []);
        setRecentIssues(response.recent_issues || []);
        setCacheMetrics(response.cache_metrics || null);
        setWorkerUtilization(response.worker_utilization || null);
        setProcessingTimeDistribution(response.processing_time_distribution || null);
        setTierMetrics(response.tier_metrics || null);
        setSecurityOverview(response.security_overview || null);
        setApiQuota(response.api_quota || null);
        setLastUpdated(new Date());
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch metrics';
      console.error('Metrics fetch error:', errorMsg);
      setError(errorMsg);

      // Only show toast for persistent errors, not initial load
      if (lastUpdated) {
        toast({
          title: 'Metrics Error',
          description: 'Failed to refresh queue metrics',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [enabled, toast, lastUpdated]);

  /**
   * Fetch specific performance stats
   */
  const fetchPerformanceStats = useCallback(async (hours: number = 24) => {
    try {
      const response = await apiClient.get<{ stats: PerformanceStats }>(
        `/monitoring/metrics/performance?hours=${hours}`
      );

      if (response?.stats) {
        setPerformanceStats(response.stats);
      }
    } catch (err) {
      console.error('Performance stats error:', err);
    }
  }, []);

  /**
   * Fetch active alerts
   */
  const fetchAlerts = useCallback(async () => {
    try {
      const response = await apiClient.get<{ alerts: Alert[] }>('/monitoring/alerts/active');

      if (response?.alerts) {
        setAlerts(response.alerts);

        // Show toast for critical alerts
        const criticalAlerts = response.alerts.filter(a =>
          a.level === 'critical' || a.level === 'emergency'
        );

        if (criticalAlerts.length > 0) {
          toast({
            title: 'Critical System Alert',
            description: criticalAlerts[0].message,
            variant: 'destructive',
          });
        }
      }
    } catch (err) {
      console.error('Alerts fetch error:', err);
    }
  }, [toast]);

  /**
   * Fetch system health status
   */
  const fetchSystemHealth = useCallback(async () => {
    try {
      const response = await apiClient.get<SystemHealth>('/monitoring/health/detailed');

      if (response) {
        setSystemHealth(response);
      }
    } catch (err) {
      console.error('System health fetch error:', err);
    }
  }, []);

  /**
   * Handle WebSocket messages for real-time updates
   */
  const handleWebSocketMessage = useCallback((message: any) => {
    if (message.type === 'metrics_update') {
      // Update current metrics from WebSocket
      if (message.data?.current) {
        setCurrentMetrics(message.data.current);
        setLastUpdated(new Date());
      }

      // Update alerts if included
      if (message.data?.alerts) {
        setAlerts(message.data.alerts);
      }
    } else if (message.type === 'alert') {
      // Handle individual alert messages
      setAlerts(prev => [message.data, ...prev].slice(0, 10)); // Keep last 10 alerts

      if (message.data.level === 'critical' || message.data.level === 'emergency') {
        toast({
          title: `${message.data.level.toUpperCase()} Alert`,
          description: message.data.message,
          variant: 'destructive',
        });
      }
    }
  }, [toast]);

  /**
   * Setup WebSocket for real-time updates
   */
  useWebSocket({
    enabled: enabled,
    onMessage: handleWebSocketMessage,
  });

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    if (enabled) {
      fetchMetrics();
      fetchSystemHealth();
    }
  }, [enabled, fetchMetrics, fetchSystemHealth]); // Only run on mount and when enabled changes

  /**
   * Setup refresh interval
   */
  useEffect(() => {
    if (!enabled || !refreshInterval || refreshInterval <= 0) {
      return;
    }

    const interval = setInterval(() => {
      fetchMetrics();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [enabled, refreshInterval, fetchMetrics]);

  /**
   * Refetch function for manual refresh
   */
  const refetch = useCallback(async () => {
    await fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Get health status based on metrics
   */
  const getHealthStatus = useCallback((): 'healthy' | 'degraded' | 'critical' => {
    if (!currentMetrics) return 'healthy';

    const { failure_rate, active_workers, queue_depth } = currentMetrics;

    if (failure_rate > 25 || active_workers === 0) {
      return 'critical';
    }

    if (failure_rate > 10 || queue_depth > 100) {
      return 'degraded';
    }

    return 'healthy';
  }, [currentMetrics]);

  /**
   * Calculate queue velocity (jobs processed per minute)
   */
  const getQueueVelocity = useCallback((): number => {
    if (!currentMetrics) return 0;

    const { jobs_per_minute, average_processing_time } = currentMetrics;

    // Calculate theoretical max throughput
    if (average_processing_time > 0) {
      const maxJobsPerMinute = 60 / average_processing_time;
      return Math.min(jobs_per_minute, maxJobsPerMinute);
    }

    return jobs_per_minute;
  }, [currentMetrics]);

  return {
    // Metrics data
    currentMetrics,
    performanceStats,
    alerts,
    recentIssues,
    cacheMetrics,
    workerUtilization,
    processingTimeDistribution,
    tierMetrics,
    securityOverview,
    apiQuota,
    systemHealth,

    // State
    isLoading,
    error,
    lastUpdated,

    // Actions
    refetch,
    fetchPerformanceStats,
    fetchAlerts,
    fetchSystemHealth,

    // Computed values
    healthStatus: getHealthStatus(),
    queueVelocity: getQueueVelocity(),
  };
};